/**
 * The L0 strip at the top of the Backup board (dashboard design rules §1):
 * the four numbers the concept names (§4), each tile a door into the section
 * that explains it.
 *
 * The honesty rule this strip exists to enforce (concept §4, `PAYLOAD.md`
 * "Backend behaviour"): **when collection has gone quiet, these tiles read
 * unknown, never green.** A silent collector and a healthy estate produce the
 * same absence of failures, and the whole point of replacing the spreadsheet is
 * that the spreadsheet could not tell those apart.
 *
 * "Did not run" gets its own tile rather than being added to "Failed" for the
 * same reason: a job that stopped being scheduled raises no error, so it is
 * invisible to any count built from errors. It is `missed` (enabled, nothing in
 * the window) plus `never_run` (enabled, nothing ever) — both are jobs with no
 * fresh restore point and no error to prove it.
 */

import { Card, Statistic } from "@norican/react";
import type { ReactNode } from "react";
import { Link } from "react-router";

import type { BackupOverview } from "@/lib/api";
import { formatCount, formatRelativeTime, type StatusTone } from "@/lib/charts/config";
import { drillTo } from "@/lib/drill";

import { BACKUP_JOBS_PATH, JOB_DRAWER_PARAM, jobKey, RESULT_PARAM } from "./params";
import { formatDuration, restorePointTone } from "./status";

const EM_DASH = "—";

interface KpiTileProps {
  /** Where the tile leads — an in-page anchor or a router path. */
  to: string;
  tone: StatusTone | "accent";
  children: ReactNode;
}

/** One KPI tile: a card that is also a link (§4.1 — no dead aggregates). */
function KpiTile({ to, tone, children }: KpiTileProps) {
  const card = (
    <Card variant="outline" accent="left" accentTone={tone} interactive>
      <Card.Body>{children}</Card.Body>
    </Card>
  );
  return to.startsWith("#") ? (
    <a href={to} className="block rounded focus-visible:outline-2">
      {card}
    </a>
  ) : (
    <Link to={to} className="block rounded focus-visible:outline-2">
      {card}
    </Link>
  );
}

export function BackupKpiStrip({ overview }: { overview: BackupOverview }) {
  /**
   * The single switch that turns the strip to unknown. Three ways in, and the
   * third is the one that used to be missing:
   *
   * - the board is not wired up;
   * - no source has reported inside twice its cadence, so the numbers describe
   *   a moment that has passed;
   * - **a read behind these numbers failed.** `service._read` degrades any
   *   failed store read to an empty default and only names it in `errors[]`, so
   *   a partial read produced `failed: 0` and a cheerful green "Nothing is in a
   *   failed state" — absence of data rendering as health, which is precisely
   *   the false assurance this board replaces the spreadsheet to remove. A
   *   non-empty `errors[]` now greys the strip, and the caption says which read
   *   failed rather than making the operator guess.
   */
  const unknown = !overview.configured || overview.stale || overview.errors.length > 0;

  const window = overview.last_24h;
  const enabled = overview.jobs_enabled;
  // Both sides of this ratio must count the same thing. `window.succeeded`
  // counts *sessions* — several for a job that runs hourly, and sessions of
  // jobs that are now disabled — so dividing it by a job count renders "96 /
  // 42" on a perfectly healthy estate. `enabled_jobs_succeeded` and
  // `enabled_jobs_ran` are the job-level counters scoped to exactly the
  // `jobs_enabled` denominator.
  const succeeded = window.enabled_jobs_succeeded;
  // Coverage: how many enabled jobs actually produced a session in the window.
  // Without it, "12 succeeded" says nothing about the other 30.
  const silent = Math.max(enabled - window.enabled_jobs_ran, 0);
  const fullCoverage = silent === 0;

  const succeededTone: StatusTone = unknown ? "neutral" : fullCoverage ? "success" : "warning";
  const failedTone: StatusTone = unknown ? "neutral" : overview.failed > 0 ? "danger" : "success";
  const didNotRunTone: StatusTone = unknown
    ? "neutral"
    : overview.did_not_run > 0
      ? "danger"
      : "success";
  const restoreTone: StatusTone = unknown
    ? "neutral"
    : overview.jobs_without_restore_point > 0
      ? "danger"
      : restorePointTone(overview.oldest_restore_point_age_sec);

  const oldestJobTo = overview.oldest_restore_point_job
    ? drillTo(BACKUP_JOBS_PATH, {
        [JOB_DRAWER_PARAM]: jobKey(
          overview.oldest_restore_point_source ?? "",
          overview.oldest_restore_point_job,
        ),
      })
    : BACKUP_JOBS_PATH;

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <KpiTile to="#backup-heat" tone={succeededTone}>
        <Statistic
          label={`Jobs succeeded · ${window.hours} h`}
          value={unknown ? EM_DASH : `${formatCount(succeeded)} / ${formatCount(enabled)}`}
          tone={succeededTone}
          size="lg"
          caption={
            unknown
              ? collectorCaption(overview)
              : fullCoverage
                ? `Every one of the ${formatCount(enabled)} enabled jobs ran`
                : `${formatCount(silent)} enabled ${
                    silent === 1 ? "job" : "jobs"
                  } produced no session — this ratio is incomplete`
          }
        />
      </KpiTile>

      <KpiTile to={drillTo(BACKUP_JOBS_PATH, { [RESULT_PARAM]: "failed" })} tone={failedTone}>
        <Statistic
          label="Failed"
          value={unknown ? EM_DASH : formatCount(overview.failed)}
          tone={failedTone}
          size="lg"
          caption={
            unknown
              ? collectorCaption(overview)
              : overview.failed > 0
                ? "Jobs that ran and did not complete"
                : "Nothing is in a failed state"
          }
        />
      </KpiTile>

      <KpiTile
        to={drillTo(BACKUP_JOBS_PATH, { [RESULT_PARAM]: ["missed", "never-run"] })}
        tone={didNotRunTone}
      >
        <Statistic
          label="Did not run"
          value={unknown ? EM_DASH : formatCount(overview.did_not_run)}
          tone={didNotRunTone}
          size="lg"
          caption={
            unknown
              ? collectorCaption(overview)
              : overview.did_not_run > 0
                ? `${formatCount(overview.missed)} missed the window · ${formatCount(
                    overview.never_run,
                  )} never ran — neither raises an error`
                : "Every enabled job produced a session"
          }
        />
      </KpiTile>

      <KpiTile to={oldestJobTo} tone={restoreTone}>
        <Statistic
          label="Oldest restore point"
          value={
            unknown || !overview.oldest_restore_point
              ? EM_DASH
              : formatRelativeTime(overview.oldest_restore_point)
          }
          tone={restoreTone}
          size="lg"
          caption={
            unknown
              ? collectorCaption(overview)
              : overview.jobs_without_restore_point > 0
                ? `${formatCount(overview.jobs_without_restore_point)} enabled ${
                    overview.jobs_without_restore_point === 1 ? "job has" : "jobs have"
                  } no restore point at all — worse than an old one, and kept out of this figure`
                : overview.oldest_restore_point
                  ? `${formatDuration(overview.oldest_restore_point_age_sec)} old${
                      overview.oldest_restore_point_job
                        ? ` · ${overview.oldest_restore_point_job}`
                        : ""
                    }`
                  : "No enabled job has a proven restore point yet"
          }
        />
      </KpiTile>
    </div>
  );
}

/** Why a tile is unknown — the collector's silence or a failed read, named. */
function collectorCaption(overview: BackupOverview): string {
  if (!overview.configured) {
    return overview.status_detail;
  }
  // A failed read is a different fault from a silent collector — one is NOVA's
  // database, the other is an on-prem scheduled task — and they send whoever
  // reads this to different places. So it is named first and named as itself.
  if (overview.errors.length > 0) {
    return `Unknown — part of this could not be read: ${overview.errors[0]}`;
  }
  return overview.collected_at
    ? `Unknown — nothing collected since ${formatRelativeTime(overview.collected_at)}`
    : "Unknown — no collection has arrived yet";
}
